import React, { useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiCurrencyUsd, mdiEmailOutline, mdiAlertOutline, mdiAccountCashOutline, mdiCreditCardSyncOutline, mdiQrcode, mdiCashRefund } from "@mdi/js";
import QRCode from "qrcode";
import { toast } from "react-toastify";
import BaseModal from "../BaseModal";
import EmailReviewModal from "../communications/EmailReviewModal";
import MarkPaidActions from "./MarkPaidActions";
import { classApi } from "../../api/classes";
import { formatCurrencyFromMinor } from "../../utils/currency";
import usePayoutsReady from "../../hooks/usePayoutsReady";
import "./AdminPaymentModal.css";

const CLASS_TYPE_LABELS = {
    "term-class": "Term Classes",
    "short-course": "Short Courses",
    "drop-in": "Drop-in Classes",
    subscription: "Subscription",
};

/**
 * AdminPaymentModal
 * Steps:
 *   'calculating' → fetch calculatePayment
 *   'selecting'   → admin picks a plan per enrolment group; groups shown with dividers
 *   'summary'     → confirm selected plans + optional custom amount override
 *   'qr'          → QR code displayed (covers all selected enrolments combined)
 *   'external'    → mark as paid outside the platform (e.g. via bank transfer or other means)
 *   'paid'        → already paid (skip plan selection entirely)
 *   'error'       → fetch error
 */
const AdminPaymentModal = ({ isOpen, onClose, onSuccess, studentProfileId, orgId, studentName, termId, trialMode = false, trialIsPaid = false, offersRefunds = false }) => {
    const initialStep = trialMode ? (trialIsPaid ? "paid" : "external") : "calculating";
    const [step, setStep] = useState(initialStep);
    const [paymentSummary, setPaymentSummary] = useState(null);
    // Partial-refund-from-payment-modal state (only when the school offers refunds)
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundDollars, setRefundDollars] = useState("");
    const [refundBusy, setRefundBusy] = useState(false);
    // selectedOptions: { [classType]: planObject | null }
    const [selectedOptions, setSelectedOptions] = useState({});
    const [customAmount, setCustomAmount] = useState("");
    const [useCustom, setUseCustom] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [qrInfo, setQrInfo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSendingEmail] = useState(false);
    const [showEmailReview, setShowEmailReview] = useState(false);
    const [isConfirmingEmailSend, setIsConfirmingEmailSend] = useState(false);
    const [isMarkingExternal, setIsMarkingExternal] = useState(false);
    const [externalMethod, setExternalMethod] = useState("bank_transfer");
    const [externalNotes, setExternalNotes] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [priorUnresolvedPayments, setPriorUnresolvedPayments] = useState([]);
    // false = positively not able to take card payments (no active Stripe account);
    // null = unknown (loading/fetch failed) — never block on unknown.
    const payoutsReady = usePayoutsReady(orgId, isOpen);
    const cardPaymentsBlocked = payoutsReady === false;

    // Issue a PARTIAL refund from the payment modal (keeps the student enrolled). A full refund
    // that also removes the student is intentionally done from the student profile instead.
    const handlePartialRefund = async () => {
        // A fully-paid student's calculate response has no enrolmentGroups (nothing left to
        // charge), so fall back to the covering paid payment's enrolment ids (paidEnrolmentIds).
        const enrolmentId =
            paymentSummary?.enrolmentGroups?.[0]?.enrolments?.[0]?.id ||
            paymentSummary?.paidEnrolmentIds?.[0];
        if (!enrolmentId) {
            toast.error("Could not find the enrolment to refund.");
            return;
        }
        const cents = Math.round(parseFloat(refundDollars) * 100);
        if (!cents || cents <= 0) {
            toast.error("Enter a refund amount greater than $0");
            return;
        }
        setRefundBusy(true);
        try {
            const r = await classApi.refundEnrolment(enrolmentId, cents);
            if (r.ok) {
                toast.success(`Partial refund of ${formatCurrencyFromMinor(cents)} issued.`);
                setRefundOpen(false);
                setRefundDollars("");
                onSuccess?.();
                onClose?.();
            } else {
                toast.error(r.data?.message || "Failed to issue refund");
            }
        } catch {
            toast.error("Failed to issue refund");
        } finally {
            setRefundBusy(false);
        }
    };

    // True when the current enrolment groups contain at least one enrolment NOT already covered
    // by the existing pending (unpaid) payment — i.e. a new enrolment was added on top of an
    // unpaid package, so the admin should be warned + pick fresh rather than silently re-using it.
    const hasUncoveredEnrolment = (groups, pending) => {
        const covered = new Set((pending?.enrolmentIds || []).map(String));
        return (groups || []).some(g =>
            (g.enrolments || []).some(e => !covered.has(String(e.id)))
        );
    };

    useEffect(() => {
        if (!isOpen) return;
        setStep("calculating");
        setPaymentSummary(null);
        setSelectedOptions({});
        setQrDataUrl(null);
        setQrInfo(null);
        setUseCustom(false);
        setCustomAmount("");
        setExternalMethod("bank_transfer");
        setExternalNotes("");
        setErrorMsg("");
        setPriorUnresolvedPayments([]);

        if (trialMode) {
            setStep(trialIsPaid ? "paid" : "external");
            return;
        }

        const fetchData = async () => {
            try {
                const calcResponse = await classApi.calculatePayment(studentProfileId, orgId, undefined, termId);

                if (!calcResponse.ok) {
                    setErrorMsg(calcResponse.data?.message || "Could not calculate payment");
                    setStep("error");
                    return;
                }

                setPaymentSummary(calcResponse.data);

                if (calcResponse.data.isPaid) {
                    setStep("paid");
                    return;
                }

                const priorRecords = calcResponse.data.priorUnresolvedPayments || [];
                if (priorRecords.length > 0) {
                    setPriorUnresolvedPayments(priorRecords);
                    setStep("consolidate-prior");
                    return;
                }

                const groups = calcResponse.data.enrolmentGroups || [];

                if (calcResponse.data.instalmentsMode) {
                    // Plan known from payment history — pre-select it.
                    // Skip to summary only if all groups have a known plan;
                    // otherwise show selecting so admin can pick plans for new enrolment types.
                    const preSelected = {};
                    let allGroupsKnown = true;
                    for (const g of groups) {
                        const knownPlan = g.applicableOptions.find(o => o.id === calcResponse.data.pricingOptionId);
                        preSelected[g.classType] = knownPlan || null;
                        if (!knownPlan) allGroupsKnown = false;
                    }
                    setSelectedOptions(preSelected);
                    setStep(allGroupsKnown ? "summary" : "selecting");
                    return;
                }

                const pending = calcResponse.data.existingPendingPayment;
                if (pending?.pricingOptionId) {
                    // Distinguish two cases:
                    //  (a) Re-opening the modal for the SAME enrolments the pending package already
                    //      covers → show the existing pending summary (pre-select, skip to summary).
                    //  (b) A NEW enrolment has been added on top of an existing unpaid package →
                    //      warn the admin that assigning a package will replace the unpaid one, then
                    //      let them pick fresh (no pre-selection — same as a first-time assignment).
                    if (hasUncoveredEnrolment(groups, pending)) {
                        const preSelected = {};
                        for (const g of groups) preSelected[g.classType] = null;
                        setSelectedOptions(preSelected);
                        setStep("replace-warning");
                        return;
                    }
                    const preSelected = {};
                    let allGroupsMatched = true;
                    for (const g of groups) {
                        const matchedPlan = g.applicableOptions.find(o => o.id === pending.pricingOptionId);
                        preSelected[g.classType] = matchedPlan || null;
                        if (!matchedPlan) allGroupsMatched = false;
                    }
                    setSelectedOptions(preSelected);
                    setStep(allGroupsMatched ? "summary" : "selecting");
                    return;
                }

                // First payment — admin picks the plan manually
                const preSelected = {};
                for (const g of groups) {
                    preSelected[g.classType] = null;
                }
                setSelectedOptions(preSelected);
                setStep("selecting");
            } catch {
                setErrorMsg("Could not load payment summary");
                setStep("error");
            }
        };
        fetchData();
    }, [isOpen, studentProfileId, orgId]);

    const handleSelectPlan = (classType, plan) => {
        setSelectedOptions(prev => ({ ...prev, [classType]: plan }));
    };

    const handleProceedToSummary = () => {
        const groups = paymentSummary?.enrolmentGroups || [];
        const anySelected = groups.some(g => selectedOptions[g.classType] != null);
        if (!anySelected) {
            toast.error("Please select a pricing package for at least one group");
            return;
        }
        setStep("summary");
    };

    const combinedTotal = () => {
        if (useCustom && customAmount) return Math.round(parseFloat(customAmount) * 100);
        return Object.values(selectedOptions).reduce((sum, opt) => sum + (opt?.price || 0), 0);
    };

    const handleGenerateQr = async () => {
        setIsGenerating(true);
        try {
            const customAmountCents =
                useCustom && customAmount ? Math.round(parseFloat(customAmount) * 100) : combinedTotal();

            // Only include enrolments from groups where admin selected a plan
            const groups = paymentSummary?.enrolmentGroups || [];
            const selectedGroups = groups.filter(g => selectedOptions[g.classType] != null);
            const selectedEnrolmentIds = selectedGroups.flatMap(g => g.enrolments.map(e => e.id));

            // Primary option = first selected group's plan (backend uses this if no custom amount)
            const primaryGroup = selectedGroups[0];
            const primaryOptionId = primaryGroup ? selectedOptions[primaryGroup.classType]?.id : undefined;

            const response = await classApi.generateQrToken(
                studentProfileId,
                orgId,
                customAmountCents,
                primaryOptionId,
                selectedEnrolmentIds
            );
            if (!response.ok) {
                toast.error(response.data?.message || "Failed to generate QR code");
                return;
            }
            const { qrUrl, qrExpiresAt, totalDue, currency } = response.data;
            const dataUrl = await QRCode.toDataURL(qrUrl, { width: 240, margin: 2 });
            setQrDataUrl(dataUrl);
            setQrInfo({ qrUrl, qrExpiresAt, totalDue, currency });
            setStep("qr");
        } catch {
            toast.error("Failed to generate QR code");
        } finally {
            setIsGenerating(false);
        }
    };

    const openEmailReview = () => {
        setShowEmailReview(true);
    };

    const handleConfirmEmailSend = async (subject, bodyHtml) => {
        setIsConfirmingEmailSend(true);
        try {
            const customAmountCents =
                useCustom && customAmount ? Math.round(parseFloat(customAmount) * 100) : combinedTotal();
            const groups = paymentSummary?.enrolmentGroups || [];
            const selectedGroups = groups.filter(g => selectedOptions[g.classType] != null);
            const selectedEnrolmentIds = selectedGroups.flatMap(g => g.enrolments.map(e => e.id));
            const primaryGroup = selectedGroups[0];
            const primaryOptionId = primaryGroup ? selectedOptions[primaryGroup.classType]?.id : undefined;
            const r = await classApi.sendPaymentEmail(
                studentProfileId,
                orgId,
                customAmountCents,
                selectedEnrolmentIds,
                subject,
                bodyHtml,
                primaryOptionId
            );
            if (r.ok) {
                toast.success("Payment reminder email sent");
                setShowEmailReview(false);
                onSuccess?.();
                onClose();
            } else {
                toast.error(r.data?.message || "Failed to send email");
            }
        } catch {
            toast.error("Failed to send email");
        } finally {
            setIsConfirmingEmailSend(false);
        }
    };

    // Returns instalment state from backend (subsequent payments) or from
    // the currently selected plan (first payment, no paid records yet).
    const getEffectiveInstalmentState = () => {
        if (paymentSummary?.instalmentsMode) {
            return {
                isInstalment: true,
                instalmentNumber: paymentSummary.instalmentNumber,
                instalmentsTotal: paymentSummary.instalmentsTotal,
                instalmentAmount: paymentSummary.instalmentAmount,
                paymentFrequency: paymentSummary.paymentFrequency,
                instalmentsPaid: paymentSummary.instalmentsPaid,
                totalPaid: paymentSummary.totalPaid,
                totalValue: paymentSummary.totalValue,
                pricingOptionId: paymentSummary.pricingOptionId || null,
            };
        }
        const groups = paymentSummary?.enrolmentGroups || [];
        const primaryGroup = groups.find(g => selectedOptions[g.classType] != null);
        const selectedPlan = primaryGroup ? selectedOptions[primaryGroup.classType] : null;
        if (selectedPlan && selectedPlan.instalments > 1 && selectedPlan.paymentType !== "once") {
            const totalValue = selectedPlan.totalValue || selectedPlan.price * selectedPlan.instalments;
            return {
                isInstalment: true,
                instalmentNumber: 1,
                instalmentsTotal: selectedPlan.instalments,
                instalmentAmount: selectedPlan.price,
                paymentFrequency: selectedPlan.paymentType,
                instalmentsPaid: 0,
                totalPaid: 0,
                totalValue,
                pricingOptionId: selectedPlan.id || null,
            };
        }
        return { isInstalment: false };
    };

    const handleMarkExternal = async () => {
        if (externalMethod === 'other' && !externalNotes.trim()) {
            toast.error("Please describe the payment method");
            return;
        }
        setIsMarkingExternal(true);
        try {
            const groups = paymentSummary?.enrolmentGroups || [];
            const selectedGroups = groups.filter(g => selectedOptions[g.classType] != null);
            const selectedEnrolmentIds = selectedGroups.flatMap(g => g.enrolments.map(e => e.id));
            const ins = getEffectiveInstalmentState();
            const instalmentContext = ins.isInstalment
                ? {
                      instalmentAmount: ins.instalmentAmount,
                      instalmentNumber: ins.instalmentNumber,
                      instalmentsTotal: ins.instalmentsTotal,
                      pricingOptionId: ins.pricingOptionId || null,
                  }
                : null;
            // Non-instalment external mark: send the asserted amount = the same integer-cents total
            // the QR/Stripe path charges (combinedTotal() — selected plan prices, or the admin's
            // custom-amount override). Without this the backend stored $0 (the bug this PRP fixes).
            // The instalment path derives its own amount from instalmentContext.
            const assertedAmountCents = ins.isInstalment ? undefined : combinedTotal();
            const r = await classApi.markExternalPayment(
                studentProfileId,
                orgId,
                externalMethod,
                externalMethod === 'other' ? externalNotes.trim() : null,
                selectedEnrolmentIds,
                instalmentContext,
                assertedAmountCents
            );
            if (r.ok) {
                toast.success("Student marked as paid (external)");
                onSuccess ? onSuccess() : onClose();
            } else {
                toast.error(r.data?.message || "Failed to mark as paid");
            }
        } catch {
            toast.error("Failed to mark as paid");
        } finally {
            setIsMarkingExternal(false);
        }
    };

    // Advance past the consolidate-prior step using the same routing as fetchData
    const proceedFromPrior = (summary) => {
        if (summary.isPaid) {
            setStep("paid");
            return;
        }
        const groups = summary.enrolmentGroups || [];
        if (summary.instalmentsMode) {
            const preSelected = {};
            let allGroupsKnown = true;
            for (const g of groups) {
                const knownPlan = g.applicableOptions.find(o => o.id === summary.pricingOptionId);
                preSelected[g.classType] = knownPlan || null;
                if (!knownPlan) allGroupsKnown = false;
            }
            setSelectedOptions(preSelected);
            setStep(allGroupsKnown ? "summary" : "selecting");
            return;
        }
        const pending = summary.existingPendingPayment;
        if (pending?.pricingOptionId) {
            if (hasUncoveredEnrolment(groups, pending)) {
                const preSelected = {};
                for (const g of groups) preSelected[g.classType] = null;
                setSelectedOptions(preSelected);
                setStep("replace-warning");
                return;
            }
            const preSelected = {};
            let allGroupsMatched = true;
            for (const g of groups) {
                const matchedPlan = g.applicableOptions.find(o => o.id === pending.pricingOptionId);
                preSelected[g.classType] = matchedPlan || null;
                if (!matchedPlan) allGroupsMatched = false;
            }
            setSelectedOptions(preSelected);
            setStep(allGroupsMatched ? "summary" : "selecting");
            return;
        }
        const preSelected = {};
        for (const g of groups) preSelected[g.classType] = null;
        setSelectedOptions(preSelected);
        setStep("selecting");
    };

    const handleResolvePriorPayment = async (record, resolution, reason) => {
        try {
            const r = await classApi.resolvePriorPayment(record.id, orgId, resolution, reason);
            if (!r.ok) {
                toast.error(r.data?.message || "Failed to resolve payment");
                return;
            }
            setPriorUnresolvedPayments(prev => prev.filter(p => p.id !== record.id));
            toast.success(resolution === "cancelled" ? "Payment record cancelled" : "Marked as paid");
        } catch {
            toast.error("Failed to resolve payment");
        }
    };

    const handleSkipPriorPayments = () => proceedFromPrior(paymentSummary);

    // Auto-advance when the admin resolves all prior records inline
    useEffect(() => {
        if (step === "consolidate-prior" && priorUnresolvedPayments.length === 0 && paymentSummary) {
            proceedFromPrior(paymentSummary);
        }
    }, [priorUnresolvedPayments, step, paymentSummary]); // eslint-disable-line react-hooks/exhaustive-deps

    // The whole modal is scoped to one org, so a single currency applies. The calc endpoint
    // returns it on every path; fall back to AUD only if it's somehow absent.
    const displayCurrency = paymentSummary?.currency || "AUD";
    const formatAmount = (cents, currency = displayCurrency) => formatCurrencyFromMinor(cents, currency);

    const renderInstalmentProgress = () => {
        if (!paymentSummary?.instalmentsMode) return null;
        const { instalmentsPaid, instalmentsTotal, instalmentAmount, totalPaid, totalValue } = paymentSummary;
        return (
            <div className="admin-payment-modal-instalment-progress">
                <p className="admin-payment-modal-instalment-label">
                    {instalmentsPaid} of {instalmentsTotal} payments made
                    {" — "}
                    {formatAmount(totalPaid)} of {formatAmount(totalValue)}
                </p>
                <div className="admin-payment-modal-instalment-bar">
                    <div
                        className="admin-payment-modal-instalment-fill"
                        style={{ width: `${(instalmentsPaid / instalmentsTotal) * 100}%` }}
                    />
                </div>
                <p className="admin-payment-modal-instalment-next">
                    Now collecting: Payment {instalmentsPaid + 1} — {formatAmount(instalmentAmount)}
                </p>
            </div>
        );
    };

    const renderPendingBanner = () => {
        const pending = paymentSummary?.existingPendingPayment;
        if (!pending?.createdAt) return null;
        const sentDate = new Date(pending.createdAt);
        const expired = pending.expiresAt && new Date(pending.expiresAt) < new Date();
        const dateStr = sentDate.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
        const expiredDateStr = pending.expiresAt
            ? new Date(pending.expiresAt).toLocaleDateString("en-AU", { day: "numeric", month: "long" })
            : null;
        return (
            <div className={`admin-payment-modal-pending-banner${expired ? " expired" : ""}`}>
                <span className="admin-payment-modal-pending-banner-dot" />
                <span>
                    {expired ? (
                        <>
                            Payment link expired{expiredDateStr ? ` on ${expiredDateStr}` : ""}. To reset, send the
                            payment email or use Pay now (QR).
                        </>
                    ) : (
                        <>
                            Package assigned, payment pending - {dateStr}
                            {pending.expiresAt && <> (Link expires {expiredDateStr})</>}
                        </>
                    )}
                </span>
            </div>
        );
    };

    const renderBody = () => {
        if (step === "calculating") {
            return <p className="admin-payment-modal-loading">Calculating payment...</p>;
        }

        if (step === "error") {
            return <p className="admin-payment-modal-error">{errorMsg}</p>;
        }

        if (step === "paid") {
            const isExternalUnconfirmed = paymentSummary?.paidOutsidePlatform === true;
            const paidLabel = paymentSummary?.instalmentsMode
                ? isExternalUnconfirmed
                    ? `All ${paymentSummary.instalmentsTotal} ${paymentSummary.paymentFrequency || "weekly"} payments marked as paid`
                    : `All ${paymentSummary.instalmentsTotal} ${paymentSummary.paymentFrequency || "weekly"} payments received`
                : isExternalUnconfirmed
                    ? "Payment marked as paid"
                    : "Payment received";
            const methodLabel = {
                stripe: "Card (Stripe)",
                bank_transfer: "Bank transfer",
                external: "External",
            }[paymentSummary?.paymentMethod] || (paymentSummary?.paymentMethod ? paymentSummary.paymentMethod : null);
            const paidDateStr = paymentSummary?.paidAt
                ? new Date(paymentSummary.paidAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                : null;
            const refundDateStr = paymentSummary?.refundedAt
                ? new Date(paymentSummary.refundedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                : null;
            return (
                <div className="admin-payment-modal-paid">
                    <p
                        className={`admin-payment-modal-paid-label${
                            isExternalUnconfirmed ? " admin-payment-modal-paid-label--unreconciled" : ""
                        }`}>
                        {paidLabel}
                    </p>
                    {paymentSummary?.pricingOptionName && (
                        <p className="admin-payment-modal-paid-plan" style={{ fontWeight: 600 }}>{paymentSummary.pricingOptionName}</p>
                    )}
                    {paymentSummary?.instalmentsMode && (
                        <p className="admin-payment-modal-paid-amount">
                            {formatAmount(paymentSummary.totalValue)} total
                        </p>
                    )}
                    {!paymentSummary?.instalmentsMode && paymentSummary?.originalAmount > 0 && (
                        <p className="admin-payment-modal-paid-amount">
                            {formatAmount(paymentSummary.originalAmount)}
                        </p>
                    )}
                    {(methodLabel || paidDateStr) && (
                        <p className="admin-payment-modal-paid-plan">
                            {[methodLabel, paidDateStr].filter(Boolean).join(" · ")}
                        </p>
                    )}
                    {paymentSummary?.referenceCode && (
                        <p className="admin-payment-modal-paid-plan" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                            {paymentSummary.referenceCode}
                        </p>
                    )}
                    {/* Refund is a SEPARATE note below the payment-received block — the method/date/
                        reference above belong to the payment received, not the refund. */}
                    {paymentSummary?.refundAmount > 0 && (
                        <div className="admin-payment-modal-refund-note">
                            <p className="admin-payment-modal-refund-note-text">
                                Note: {formatAmount(paymentSummary.refundAmount)} refunded
                                {refundDateStr ? ` on ${refundDateStr}` : ""}.
                            </p>
                            <p className="admin-payment-modal-refund-note-sub">
                                See the History tab for full refund details.
                            </p>
                        </div>
                    )}
                    {paymentSummary?.paidOutsidePlatform && (
                        <div className="admin-payment-modal-reconcile-callout">
                            <Icon
                                path={mdiCreditCardSyncOutline}
                                size={1}
                                color="var(--dl-color-greentick)"
                            />
                            <p className="admin-payment-modal-reconcile-callout-text">
                                Paid externally. Reconcile in Manage Finances (see help for more info).
                            </p>
                        </div>
                    )}
                    {offersRefunds && (
                        <div className="admin-payment-modal-refund-block">
                            {refundOpen ? (
                                <>
                                    <div className="admin-payment-modal-refund-row">
                                        <span className="admin-payment-modal-refund-currency">$</span>
                                        <input
                                            className="admin-payment-modal-refund-input"
                                            type="number"
                                            min="0"
                                            step="1"
                                            placeholder="0.00"
                                            value={refundDollars}
                                            onChange={e => setRefundDollars(e.target.value)}
                                        />
                                        <button
                                            className="btn-turquoise"
                                            onClick={handlePartialRefund}
                                            disabled={refundBusy}>
                                            {refundBusy ? "Processing..." : "Refund"}
                                        </button>
                                        <button
                                            className="admin-payment-modal-refund-cancel"
                                            onClick={() => { setRefundOpen(false); setRefundDollars(""); }}>
                                            Cancel
                                        </button>
                                    </div>
                                    <p className="admin-payment-modal-refund-hint">
                                        To refund <strong>and remove</strong> this student, use the Refund / Remove
                                        buttons on their student profile.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <button className="btn-turquoise" onClick={() => setRefundOpen(true)}>
                                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <Icon path={mdiCashRefund} size={0.8} />
                                            Issue partial refund
                                        </span>
                                    </button>
                                    <p className="admin-payment-modal-refund-hint">
                                        To refund <strong>and remove</strong> this student, use the Refund / Remove
                                        buttons on their student profile.
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (step === "consolidate-prior") {
            const formatPriorDate = (iso) =>
                iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";
            return (
                <div>
                    <div className="admin-payment-modal-warning-banner">
                        <Icon path={mdiAlertOutline} size={0.9} />
                        <p>
                            <strong>Prior unresolved payments</strong>
                            <br />
                            This student has {priorUnresolvedPayments.length} unresolved payment record{priorUnresolvedPayments.length !== 1 ? "s" : ""} from a previous term. You can resolve them now, or skip (Process current payment only) to reconcile later.
                        </p>
                    </div>
                    <div className="admin-payment-modal-prior-records">
                        {priorUnresolvedPayments.map(record => (
                            <div key={record.id} className="admin-payment-modal-prior-record-card">
                                <div className="admin-payment-modal-prior-record-info">
                                    <span className="admin-payment-modal-prior-record-term">{record.termName}</span>
                                    {record.pricingOptionName && <span>{record.pricingOptionName}</span>}
                                    <strong>{formatAmount(record.amount, record.currency)}</strong>
                                    {record.referenceCode && (
                                        <span className="admin-payment-modal-prior-record-ref">{record.referenceCode}</span>
                                    )}
                                    <span style={{ color: "var(--dl-color-gray-500)", fontSize: "0.78rem", marginLeft: "auto" }}>
                                        {formatPriorDate(record.createdAt)} · {record.isExpired ? "Link expired" : "Link active"}
                                    </span>
                                </div>
                                <div className="admin-payment-modal-prior-record-actions">
                                    <MarkPaidActions
                                        onMarkPaid={(method, notes) => {
                                            const resolution = method === "bank_transfer" ? "paid_bank_transfer" : "paid_external";
                                            const reason = method === "other" ? notes : method === "eftpos" ? "EFTPOS" : method === "cash" ? "Cash" : null;
                                            handleResolvePriorPayment(record, resolution, reason);
                                        }}
                                        onCancel={() => handleResolvePriorPayment(record, "cancelled", null)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (step === "external") {
            return (
                <div className="admin-payment-modal-external">
                    <label
                        className="admin-payment-modal-override-label"
                        style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                        How did they pay?
                    </label>
                    <select
                        value={externalMethod}
                        onChange={e => { setExternalMethod(e.target.value); setExternalNotes(""); }}
                        className="admin-payment-modal-amount-input"
                        style={{ width: "100%", padding: "8px" }}>
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="eftpos">EFTPOS</option>
                        <option value="cash">Cash</option>
                        <option value="other">Other</option>
                    </select>
                    {externalMethod === "other" && (
                        <input
                            type="text"
                            className="admin-payment-modal-amount-input"
                            style={{ width: "100%", padding: "8px", marginTop: "8px", boxSizing: "border-box" }}
                            placeholder="Describe the payment method (required)"
                            value={externalNotes}
                            onChange={e => setExternalNotes(e.target.value)}
                        />
                    )}
                </div>
            );
        }

        if (step === "qr") {
            return (
                <div className="admin-payment-modal-qr">
                    <p className="admin-payment-modal-hint-text">
                        Ask the student to scan this with their phone. Easiest if they have <strong>Apple Pay</strong>{" "}
                        or <strong>Google Pay</strong> ready. Without these, they will need to enter card details on
                        screen.
                    </p>
                    <p className="admin-payment-modal-qr-amount">{formatAmount(qrInfo.totalDue, qrInfo.currency)}</p>
                    <img src={qrDataUrl} alt="Payment QR code" className="admin-payment-modal-qr-image" />
                    <p className="admin-payment-modal-qr-hint">
                        Student scans this QR to pay.
                        {qrInfo.qrExpiresAt
                            ? ` Valid until ${new Date(qrInfo.qrExpiresAt).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}.`
                            : ""}
                    </p>
                </div>
            );
        }

        const groups = paymentSummary?.enrolmentGroups || [];

        if (step === "replace-warning") {
            const pending = paymentSummary?.existingPendingPayment;
            return (
                <div className="admin-payment-modal-selecting">
                    <div className="admin-payment-modal-warning-banner">
                        <p>
                            <strong>This student already has an unpaid package</strong>
                            {pending?.pricingOptionName ? `: ${pending.pricingOptionName}` : ""}
                            {pending?.amount != null ? ` (${formatAmount(pending.amount)})` : ""}.
                            {" "}Assigning a new package will <strong>replace</strong> the unpaid one.
                            Continue to choose the package.
                        </p>
                    </div>
                </div>
            );
        }

        if (step === "selecting") {
            return (
                <div className="admin-payment-modal-selecting">
                    <p className="admin-payment-modal-selecting-hint">
                        Select the appropriate payment package for this student's current enrolments.
                    </p>
                    {groups.map((group, gi) => (
                        <div key={group.classType} className="admin-payment-modal-group">
                            {groups.length > 1 && (
                                <div className="admin-payment-modal-group-divider">
                                    {CLASS_TYPE_LABELS[group.classType] || group.classType}
                                </div>
                            )}
                            <div className="admin-payment-modal-group-enrolments">
                                {group.enrolments.map(e => (
                                    <span key={e.id} className="admin-payment-modal-enrolment-chip">
                                        {e.className || group.classType}
                                    </span>
                                ))}
                            </div>
                            <ul className="admin-payment-modal-plans-list">
                                {group.applicableOptions.map(plan => {
                                    const isSelected = selectedOptions[group.classType]?.id === plan.id;
                                    const isInstalment = plan.instalments > 1;
                                    const planTotal = plan.totalValue || plan.price * (plan.instalments || 1);
                                    return (
                                        <li
                                            key={plan.id}
                                            className={`admin-payment-modal-plan-row${isSelected ? " selected" : ""}`}
                                            onClick={() => handleSelectPlan(group.classType, plan)}>
                                            <span className="admin-payment-modal-plan-name">{plan.name}</span>
                                            <span className="admin-payment-modal-plan-price">
                                                {isInstalment
                                                    ? `${formatAmount(plan.price)}/${plan.paymentType} × ${plan.instalments} = ${formatAmount(planTotal)}`
                                                    : formatAmount(plan.price)}
                                            </span>
                                        </li>
                                    );
                                })}
                                {group.applicableOptions.length === 0 && (
                                    <li className="admin-payment-modal-no-plans">
                                        No pricing plans configured for this type.
                                    </li>
                                )}
                                {groups.length > 1 && (
                                    <li
                                        className={`admin-payment-modal-plan-row admin-payment-modal-plan-row--skip${selectedOptions[group.classType] == null ? " selected" : ""}`}
                                        onClick={() => handleSelectPlan(group.classType, null)}>
                                        <span className="admin-payment-modal-plan-name">Pay later</span>
                                        <span className="admin-payment-modal-plan-price">—</span>
                                    </li>
                                )}
                            </ul>
                            {gi < groups.length - 1 && <hr className="admin-payment-modal-separator" />}
                        </div>
                    ))}
                    {groups.length === 0 && (
                        <p className="admin-payment-modal-no-plans">
                            <strong>No unpaid enrolments found</strong> — this student is already up to date for this term.
                        </p>
                    )}
                </div>
            );
        }

        // step === 'summary'
        const total = combinedTotal();
        const ins = getEffectiveInstalmentState();
        return (
            <div className="admin-payment-modal-summary">
                {renderPendingBanner()}
                {/* Payment method hint — top of modal, below header */}
                <p className="admin-payment-modal-hint-text">
                    The package is assigned when you choose an action below. Closing this window without
                    choosing one assigns nothing.
                </p>
                {cardPaymentsBlocked && (
                    <div className="admin-payment-modal-warning-banner">
                        <p>
                            <strong>Online payments are not set up yet.</strong> Connect your payout account in{" "}
                            <a href="/manage-finances">Manage Finances</a> to send card payment requests. You can
                            still use Mark paid to record a payment received outside the platform.
                        </p>
                    </div>
                )}
                {!cardPaymentsBlocked && (
                    <>
                        <p className="admin-payment-modal-hint-text">
                            <strong>Pay now (QR)</strong> shows a QR for instant pay on the student&apos;s phone (best with Apple Pay or Google Pay).
                        </p>
                        <p className="admin-payment-modal-hint-text">
                            <strong>OK (send email)</strong> emails a pay-later link{paymentSummary?.bankTransferEnabled ? ", including your bank transfer details," : ""} so the student pays in their own time.
                        </p>
                    </>
                )}
                {paymentSummary?.bankTransferEnabled && (
                    <p className="admin-payment-modal-hint-text">
                        <strong>Mark paid</strong> if you&apos;ve already received payment by bank transfer, EFTPOS, or cash.
                    </p>
                )}
                {renderInstalmentProgress()}
                {groups.map((group, gi) => {
                    const selected = selectedOptions[group.classType];
                    return (
                        <div key={group.classType} className="admin-payment-modal-summary-group">
                            {groups.length > 1 && (
                                <div className="admin-payment-modal-group-divider">
                                    {CLASS_TYPE_LABELS[group.classType] || group.classType}
                                </div>
                            )}
                            {!selected && (
                                <p className="admin-payment-modal-pay-later-note">
                                    Not paying today — will remain outstanding
                                </p>
                            )}
                            {selected && (
                                <>
                                    <ul className="admin-payment-modal-enrolments">
                                        {group.enrolments.map(e => (
                                            <li key={e.id} className="admin-payment-modal-enrolment-item">
                                                <span>{e.className || "—"}</span>
                                                <span className="admin-payment-modal-level">{e.level}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="admin-payment-modal-pricing-row">
                                        <span>Plan</span>
                                        <span>
                                            {selected.name} — {formatAmount(selected.price)}
                                        </span>
                                    </div>
                                </>
                            )}
                            {gi < groups.length - 1 && <hr className="admin-payment-modal-separator" />}
                        </div>
                    );
                })}

                <div className="admin-payment-modal-total">
                    <span>Total</span>
                    <span>{formatAmount(total)}</span>
                </div>

                {/* Custom amount override */}
                <div className="admin-payment-modal-override">
                    <label className="admin-payment-modal-override-label">
                        <input type="checkbox" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} />
                        {ins.isInstalment
                            ? `Override this instalment amount (default: ${formatAmount(ins.instalmentAmount)})`
                            : "Override amount (optional)"}
                    </label>
                    {useCustom && (
                        <div className="admin-payment-modal-custom-amount">
                            <span className="admin-payment-modal-currency-prefix">$</span>
                            <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="0.00"
                                value={customAmount}
                                onChange={e => setCustomAmount(e.target.value)}
                                className="admin-payment-modal-amount-input"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const canProceed = () => {
        const groups = paymentSummary?.enrolmentGroups || [];
        // Allow proceed even if no plan selected (admin can still generate a $0 or custom QR)
        return groups.length > 0;
    };

    const renderFooter = () => {
        if (step === "replace-warning") {
            return (
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={() => setStep("selecting")}>
                        Continue
                    </button>
                </>
            );
        }
        if (step === "consolidate-prior") {
            return (
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={handleSkipPriorPayments}>
                        Process current payment only
                    </button>
                </>
            );
        }
        if (step === "selecting") {
            return (
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={handleProceedToSummary} disabled={!canProceed()}>
                        Next
                    </button>
                </>
            );
        }
        if (step === "summary") {
            return (
                <div className="admin-payment-modal-summary-footer">
                    <div className="admin-payment-modal-summary-footer-row">
                        <button className="base-modal-cancel-btn" onClick={() => setStep("selecting")}>
                            Back
                        </button>
                        <div className="admin-payment-modal-summary-footer-actions">
                            {paymentSummary?.bankTransferEnabled && (
                                <button
                                    className="base-modal-secondary-btn"
                                    onClick={() => setStep("external")}
                                    disabled={isGenerating || isSendingEmail}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <Icon path={mdiAccountCashOutline} size={0.85} />
                                        Mark paid
                                    </span>
                                </button>
                            )}
                            <button
                                className="base-modal-save-btn"
                                onClick={handleGenerateQr}
                                disabled={isGenerating || isSendingEmail || cardPaymentsBlocked}
                                title={cardPaymentsBlocked ? "Connect your payout account in Manage Finances first" : undefined}>
                                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <Icon path={mdiQrcode} size={0.9} />
                                    {isGenerating ? "Loading..." : "Pay now (QR)"}
                                </span>
                            </button>
                            <button
                                className="base-modal-secondary-btn"
                                onClick={openEmailReview}
                                disabled={isSendingEmail || isGenerating || cardPaymentsBlocked}
                                title={cardPaymentsBlocked ? "Connect your payout account in Manage Finances first" : undefined}>
                                <Icon path={mdiEmailOutline} size={1} />
                                OK (send email)
                            </button>
                        </div>
                    </div>
                    {!paymentSummary?.bankTransferEnabled && (
                        <button
                            className="admin-payment-modal-external-link"
                            onClick={() => setStep("external")}
                            disabled={isGenerating || isSendingEmail}>
                            <Icon path={mdiAccountCashOutline} size={0.75} />
                            Mark paid outside platform?
                        </button>
                    )}
                </div>
            );
        }
        if (step === "external") {
            return (
                <>
                    <button className="base-modal-cancel-btn" onClick={() => setStep("summary")}>
                        Back
                    </button>
                    <button
                        className="base-modal-save-btn"
                        onClick={handleMarkExternal}
                        disabled={isMarkingExternal || (externalMethod === "other" && !externalNotes.trim())}>
                        {isMarkingExternal ? "Saving..." : "Mark as Paid"}
                    </button>
                </>
            );
        }
        if (step === "qr") {
            return (
                <>
                    <button className="base-modal-cancel-btn" onClick={() => setStep("summary")}>
                        Back
                    </button>
                    <button className="base-modal-save-btn" onClick={onClose}>
                        Done
                    </button>
                </>
            );
        }
        return (
            <button className="base-modal-cancel-btn" onClick={onClose}>
                Close
            </button>
        );
    };

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title={`Payment — ${studentName}`}
                icon={mdiCurrencyUsd}
                maxWidth={620}
                footer={renderFooter()}>
                {renderBody()}
            </BaseModal>
            <EmailReviewModal
                isOpen={showEmailReview}
                emailType="enrolmentPayment"
                templateData={{
                    firstName: studentName?.split(" ")[0] || "Student",
                    // Pass the raw subtotal in cents so the preview backend can derive
                    // the service fee + total (mirrors the send path). formattedAmount is
                    // recomputed server-side from amount; sent here only as a fallback.
                    amount: combinedTotal(),
                    currency: displayCurrency,
                    formattedAmount: formatCurrencyFromMinor(combinedTotal(), displayCurrency),
                }}
                audienceDescription={`To ${studentName || 'this student'}`}
                recipientCount={1}
                onConfirm={handleConfirmEmailSend}
                onCancel={() => setShowEmailReview(false)}
                isConfirming={isConfirmingEmailSend}
            />
        </>
    );
};

export default AdminPaymentModal;
